import { useColorMode } from '@vueuse/core'
import { watch } from 'vue'
import { useRoute } from 'vue-router'
import { reapplyBrandTheme } from './useBrandTheme'

/**
 * Composable for managing application theme with route-based governance.
 * Restrictions: Dark mode is only applied on authenticated routes.
 */
export function useTheme() {
  const route = useRoute()

  // Helper to determine if a path is a guest/unauthenticated route
  const checkIsGuest = (path: string) => {
    return (
      path === '/login' ||
      path === '/reset-password' ||
      path === '/set-password' ||
      path === '/access-denied' ||
      path.startsWith('/admin/resellers/preview/')
    )
  }

  // Initialize color mode synced with localStorage
  const mode = useColorMode({
    storageKey: 'data-theme',
    onChanged: (mode, defaultHandler) => {
      // Synchronous path check to prevent dark mode during early boot before router is ready
      const isGuest = checkIsGuest(window.location.pathname)
      if (!isGuest) {
        defaultHandler(mode)
      } else {
        document.documentElement.classList.remove('dark')
      }
      // After `defaultHandler` updates `.dark` on <html> (VueUse flush: post). Brand
      // primary lightness depends on dark mode; running earlier leaves light-mode inline
      // `--primary` overrides that block `.dark` tokens until a full reload.
      reapplyBrandTheme()
    }
  })

  /**
   * Initializes the theme governance watcher.
   * Should be called once in App.vue or the root component.
   */
  const initThemeGovernance = () => {
    // Applies or removes dark mode when entering/leaving authenticated routes.
    watch(
      [() => route.meta.requiresAuth, () => route.path],
      ([requiresAuth, path]) => {
        const isGuest = checkIsGuest(path) || requiresAuth === false
        if (!isGuest) {
          // Re-apply the user's preferred mode when entering an auth route
          document.documentElement.classList.toggle('dark', mode.value === 'dark')
        } else {
          // Force light mode on guest/unauthenticated routes
          document.documentElement.classList.remove('dark')
        }
        reapplyBrandTheme()
      },
      { immediate: true }
    )
  }

  return {
    mode,
    initThemeGovernance
  }
}
